import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Webcam from 'react-webcam';
import API from '../utils/api';
import './Psychotest.css';
import './Exam.css';

const FACE_CHECK_INTERVAL = 2000;
const FACE_MISSING_THRESHOLD = 5000;

export default function Psychotest() {
  const navigate = useNavigate();
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [currentIdx, setCurrentIdx] = useState(0);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [psychotestStarted, setPsychotestStarted] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [cameraError, setCameraError] = useState(false);
  const [faceStatus, setFaceStatus] = useState('detecting');
  const [rulesConfirmed, setRulesConfirmed] = useState(false);
  const [durationMinutes, setDurationMinutes] = useState(30);
  const [remainingSeconds, setRemainingSeconds] = useState(0);
  const [totalQuestionsSetting, setTotalQuestionsSetting] = useState(0);
  const [maxViolations, setMaxViolations] = useState(3);
  const [violationCount, setViolationCount] = useState(0);
  const [disqualified, setDisqualified] = useState(false);

  const webcamRef = useRef(null);
  const canvasRef = useRef(null);
  const faceTimerRef = useRef(null);
  const timerRef = useRef(null);
  const autoSubmittedRef = useRef(false);
  const violationLockRef = useRef(false);
  const faceMissingStartRef = useRef(null);
  const disqualifySentRef = useRef(false);

  const isMobileDevice = useMemo(() => {
    const ua = navigator.userAgent || '';
    const mobileUA = /Android|iPhone|iPad|iPod|Windows Phone|Mobile/i.test(ua);
    const coarsePointer = typeof window !== 'undefined' && window.matchMedia
      ? window.matchMedia('(pointer: coarse)').matches
      : false;
    const smallScreen = typeof window !== 'undefined' ? window.innerWidth <= 1024 : false;
    return mobileUA || (coarsePointer && smallScreen);
  }, []);
  const requireFullscreen = !isMobileDevice;
  const webcamVideoConstraints = useMemo(
    () => ({
      facingMode: { ideal: 'user' },
      width: { ideal: 640 },
      height: { ideal: 480 },
    }),
    [isMobileDevice]
  );

  const handleCameraError = useCallback(() => {
    setCameraError(true);
    setError(
      isMobileDevice
        ? 'Kamera depan wajib digunakan. Pastikan izin kamera diberikan dan pilih kamera depan pada perangkat Anda.'
        : 'Kamera tidak tersedia. Pastikan izin kamera diberikan.'
    );
  }, [isMobileDevice]);

  const handleUserMedia = useCallback((stream) => {
    if (!isMobileDevice) {
      setCameraError(false);
      return;
    }

    const track = stream?.getVideoTracks?.()[0];
    const settings = track?.getSettings ? track.getSettings() : {};
    const facingMode = String(settings?.facingMode || '').toLowerCase();
    const label = String(track?.label || '').toLowerCase();
    const looksBackCamera =
      facingMode === 'environment' ||
      label.includes('back') ||
      label.includes('rear') ||
      label.includes('environment');

    if (looksBackCamera) {
      stream.getTracks().forEach((t) => t.stop());
      setCameraError(true);
      setError('Kamera depan wajib digunakan. Silakan pilih front camera pada browser iPhone Anda.');
      return;
    }

    setCameraError(false);
    setError((prev) => (prev && prev.includes('Kamera depan wajib') ? '' : prev));
  }, [isMobileDevice]);

  const saveInitialPsychotestFacePhoto = useCallback(async (attemptId) => {
    if (!attemptId || !webcamRef.current?.getScreenshot) return;

    try {
      const screenshot = webcamRef.current.getScreenshot();
      if (!screenshot) return;
      await API.post('/psychotest/save-face', {
        attempt_id: attemptId,
        screenshot,
      });
    } catch (err) {
      console.error('Save psychotest face photo error:', err);
    }
  }, []);

  useEffect(() => {
    const load = async () => {
      try {
        const [statusRes, questionRes] = await Promise.all([
          API.get('/psychotest/status'),
          API.get('/psychotest/questions'),
        ]);

        if (statusRes.data?.completed) {
          navigate('/dashboard');
          return;
        }

        setQuestions(questionRes.data?.questions || []);
        setDurationMinutes(Number(questionRes.data?.duration_minutes || statusRes.data?.duration_minutes || 30));
        setTotalQuestionsSetting(Number(questionRes.data?.total_questions_setting || statusRes.data?.total_questions_setting || 0));
        setMaxViolations(Number(questionRes.data?.max_violations_setting || statusRes.data?.max_violations_setting || 3));
      } catch (err) {
        setError(err.response?.data?.message || 'Gagal memuat psikotest');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [navigate]);

  const answeredCount = useMemo(() => {
    return Object.keys(answers).filter((k) => !!answers[k]).length;
  }, [answers]);

  const handlePick = (questionId, value) => {
    setAnswers((prev) => ({ ...prev, [questionId]: value }));
  };

  const enterFullscreen = useCallback(async () => {
    try {
      const el = document.documentElement;
      if (el.requestFullscreen) {
        await el.requestFullscreen();
      } else if (el.mozRequestFullScreen) {
        await el.mozRequestFullScreen();
      } else if (el.webkitRequestFullscreen) {
        await el.webkitRequestFullscreen();
      }
      setIsFullscreen(true);
    } catch (err) {
      // no-op
    }
  }, []);

  useEffect(() => {
    const checkFullscreen = () => {
      const isFull =
        !!document.fullscreenElement ||
        !!document.webkitFullscreenElement ||
        !!document.mozFullScreenElement;
      setIsFullscreen(isFull);
    };

    checkFullscreen();
    document.addEventListener('fullscreenchange', checkFullscreen);
    document.addEventListener('webkitfullscreenchange', checkFullscreen);
    document.addEventListener('mozfullscreenchange', checkFullscreen);

    return () => {
      document.removeEventListener('fullscreenchange', checkFullscreen);
      document.removeEventListener('webkitfullscreenchange', checkFullscreen);
      document.removeEventListener('mozfullscreenchange', checkFullscreen);
    };
  }, []);

  const checkFace = useCallback(() => {
    if (!webcamRef.current || !canvasRef.current) return;
    const video = webcamRef.current.video;
    if (!video || video.readyState !== 4) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    canvas.width = 160;
    canvas.height = 120;
    ctx.drawImage(video, 0, 0, 160, 120);

    const imgData = ctx.getImageData(0, 0, 160, 120);
    const pixels = imgData.data;
    let skinPixels = 0;

    for (let i = 0; i < pixels.length; i += 4) {
      const r = pixels[i];
      const g = pixels[i + 1];
      const b = pixels[i + 2];
      if (r > 80 && g > 40 && b > 20 && r > g && r > b && (r - g) > 15 && r < 250) {
        skinPixels++;
      }
    }

    const ratio = skinPixels / (160 * 120);
    const detected = ratio > 0.05;
    setFaceStatus(detected ? 'ok' : 'missing');

    if (detected) {
      faceMissingStartRef.current = null;
    }
  }, []);

  const registerViolation = useCallback((message) => {
    if (!psychotestStarted || disqualified) return;
    if (violationLockRef.current) return;

    violationLockRef.current = true;
    setError(message);
    setViolationCount((prev) => {
      const next = prev + 1;
      if (next >= maxViolations) {
        setDisqualified(true);
      }
      return next;
    });

    setTimeout(() => {
      violationLockRef.current = false;
    }, 1800);
  }, [psychotestStarted, disqualified, maxViolations]);

  useEffect(() => {
    if (cameraError) return;
    faceTimerRef.current = setInterval(checkFace, FACE_CHECK_INTERVAL);
    return () => clearInterval(faceTimerRef.current);
  }, [cameraError, checkFace]);

  useEffect(() => {
    if (!psychotestStarted || disqualified) return;
    if (faceStatus !== 'missing') return;

    if (!faceMissingStartRef.current) {
      faceMissingStartRef.current = Date.now();
      return;
    }

    if (Date.now() - faceMissingStartRef.current >= FACE_MISSING_THRESHOLD) {
      faceMissingStartRef.current = Date.now();
      registerViolation('Wajah tidak terdeteksi lebih dari 5 detik.');
    }
  }, [faceStatus, psychotestStarted, disqualified, registerViolation]);

  useEffect(() => {
    if (!psychotestStarted || isMobileDevice) return;

    const handleBlur = () => {
      registerViolation('Jangan pindah tab/jendela selama psikotest berlangsung.');
    };
    const handleVisibility = () => {
      if (document.hidden) {
        registerViolation('Anda meninggalkan halaman psikotest. Fokus kembali untuk melanjutkan.');
      }
    };
    const blockCopyPaste = (e) => {
      e.preventDefault();
      registerViolation('Copy-paste dinonaktifkan selama psikotest berlangsung.');
    };

    window.addEventListener('blur', handleBlur);
    document.addEventListener('visibilitychange', handleVisibility);
    document.addEventListener('copy', blockCopyPaste);
    document.addEventListener('paste', blockCopyPaste);
    document.addEventListener('cut', blockCopyPaste);

    return () => {
      window.removeEventListener('blur', handleBlur);
      document.removeEventListener('visibilitychange', handleVisibility);
      document.removeEventListener('copy', blockCopyPaste);
      document.removeEventListener('paste', blockCopyPaste);
      document.removeEventListener('cut', blockCopyPaste);
    };
  }, [psychotestStarted, registerViolation, isMobileDevice]);

  useEffect(() => {
    if (!requireFullscreen) {
      setIsFullscreen(true);
      return undefined;
    }

    const checkFullscreen = () => {
      const isFull =
        !!document.fullscreenElement ||
        !!document.webkitFullscreenElement ||
        !!document.mozFullScreenElement;

      if (psychotestStarted && !isFull && !disqualified) {
        registerViolation('Keluar dari mode fullscreen saat psikotest berlangsung.');
      }
    };

    document.addEventListener('fullscreenchange', checkFullscreen);
    document.addEventListener('webkitfullscreenchange', checkFullscreen);
    document.addEventListener('mozfullscreenchange', checkFullscreen);

    return () => {
      document.removeEventListener('fullscreenchange', checkFullscreen);
      document.removeEventListener('webkitfullscreenchange', checkFullscreen);
      document.removeEventListener('mozfullscreenchange', checkFullscreen);
    };
  }, [psychotestStarted, disqualified, registerViolation, requireFullscreen]);

  const fullscreenReady = requireFullscreen ? isFullscreen : true;
  const canStartPsychotest = fullscreenReady && !cameraError && faceStatus === 'ok' && rulesConfirmed;
  const currentQ = questions[currentIdx];
  const progressPercent = questions.length > 0 ? ((currentIdx + 1) / questions.length) * 100 : 0;

  const formatTime = (s) => {
    const m = Math.floor(s / 60).toString().padStart(2, '0');
    const sec = (s % 60).toString().padStart(2, '0');
    return `${m}:${sec}`;
  };

  useEffect(() => {
    if (!psychotestStarted || remainingSeconds <= 0) return;

    timerRef.current = setInterval(() => {
      setRemainingSeconds((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timerRef.current);
  }, [psychotestStarted, remainingSeconds]);

  useEffect(() => {
    if (!psychotestStarted) return;
    if (remainingSeconds > 0) return;
    if (autoSubmittedRef.current) return;

    autoSubmittedRef.current = true;
    handleSubmit(true);
  }, [remainingSeconds, psychotestStarted]);

  useEffect(() => {
    if (!disqualified || !psychotestStarted) return;
    if (disqualifySentRef.current) return;

    disqualifySentRef.current = true;
    clearInterval(timerRef.current);

    API.post('/psychotest/disqualify', {
      violation_count: violationCount,
      reason: 'Melebihi batas maksimal pelanggaran',
    }).catch(() => {
      // Keep disqualified UI even if network call fails; user is still blocked client-side.
    });
  }, [disqualified, psychotestStarted, violationCount]);

  const startPsychotestFlow = async () => {
    if (requireFullscreen && !isFullscreen) {
      setError('Aktifkan mode layar penuh terlebih dahulu.');
      return;
    }
    if (cameraError) {
      setError('Kamera tidak tersedia. Pastikan izin kamera diberikan.');
      return;
    }
    if (faceStatus !== 'ok') {
      setError('Wajah belum terdeteksi dengan baik. Pastikan wajah terlihat jelas di kamera.');
      return;
    }
    if (!rulesConfirmed) {
      setError('Centang persetujuan peraturan sebelum mulai psikotest.');
      return;
    }

    try {
      const startRes = await API.post('/psychotest/start');
      const serverRemaining = Number(startRes.data?.remainingSeconds || 0);
      const serverDuration = Number(startRes.data?.duration_minutes || durationMinutes || 30);
      const startedQuestions = Array.isArray(startRes.data?.questions) ? startRes.data.questions : [];
      const serverTotalSetting = Number(startRes.data?.total_questions_setting || totalQuestionsSetting || 0);
      const serverMaxViolations = Number(startRes.data?.max_violations_setting || maxViolations || 3);
      setDurationMinutes(serverDuration);
      setTotalQuestionsSetting(serverTotalSetting);
      setMaxViolations(serverMaxViolations);
      setRemainingSeconds(serverRemaining > 0 ? serverRemaining : serverDuration * 60);
      if (startedQuestions.length > 0) {
        setQuestions(startedQuestions);
      }
      setCurrentIdx(0);
      setAnswers({});
      setViolationCount(0);
      setDisqualified(false);
      disqualifySentRef.current = false;
      faceMissingStartRef.current = null;
      autoSubmittedRef.current = false;
      setError('');
      setPsychotestStarted(true);
      await saveInitialPsychotestFacePhoto(startRes.data?.attempt_id);
    } catch (err) {
      setError(err.response?.data?.message || 'Gagal memulai psikotest');
    }
  };

  const handleSubmit = async (isAutoTimeout = false) => {
    if (!isAutoTimeout && answeredCount !== questions.length) {
      setError('Semua pertanyaan psikotest wajib diisi sebelum lanjut ke exam.');
      return;
    }

    if (isAutoTimeout && answeredCount !== questions.length) {
      setError('Waktu psikotest habis sebelum semua jawaban terisi. Silakan mulai ulang psikotest.');
      setSubmitting(false);
      setPsychotestStarted(false);
      setCurrentIdx(0);
      setAnswers({});
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      const payload = {
        answers: questions.map((q) => ({
          question_id: q.id,
          value: String(answers[q.id]),
        })),
      };

      await API.post('/psychotest/submit', payload);
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.message || 'Gagal menyimpan hasil psikotest');
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="psy-loading">Memuat psikotest...</div>;
  }

  if (disqualified) {
    return (
      <div className="disqualified-screen">
        <div className="disq-card">
          <div className="disq-icon">🚫</div>
          <h1>Anda Didiskualifikasi</h1>
          <p>Anda telah melakukan lebih dari {maxViolations} pelanggaran pada psikotest.</p>
          <p>Psikotest dinyatakan <strong>TIDAK VALID</strong>.</p>
          <button onClick={() => navigate('/dashboard')} className="btn-submit">Kembali ke Dashboard</button>
        </div>
      </div>
    );
  }

  return (
    <div className="exam-page psychotest-page">
      {requireFullscreen && !isFullscreen && (
        <div className="fullscreen-prompt">
          <div className="fs-card">
            <h2>⛶ Mode Layar Penuh Diperlukan</h2>
            <p>Psikotest harus dijalankan dalam mode layar penuh</p>
            <button onClick={enterFullscreen} className="btn-submit">Aktifkan Layar Penuh</button>
          </div>
        </div>
      )}

      {error && <div className="psy-error">{error}</div>}

      {!psychotestStarted ? (
        <div className="precheck-screen">
          <section className="precheck-card">
            <h2>Persiapan Sebelum Mulai Psikotest</h2>
            <p className="precheck-subtitle">Syarat pengerjaan psikotest sama seperti exam. Pastikan semua status sudah siap.</p>

            <div className="precheck-grid">
              <div className="precheck-item">
                <span className="check-dot ok"></span>
                <div>
                  <strong>Durasi Psikotest</strong>
                  <p>{durationMinutes} menit</p>
                </div>
              </div>

              <div className="precheck-item">
                <span className="check-dot ok"></span>
                <div>
                  <strong>Jumlah Soal Ditampilkan</strong>
                  <p>{totalQuestionsSetting > 0 ? `${totalQuestionsSetting} soal` : 'Semua soal aktif'}</p>
                </div>
              </div>

              <div className="precheck-item">
                <span className="check-dot ok"></span>
                <div>
                  <strong>Maksimal Pelanggaran</strong>
                  <p>{maxViolations} kali</p>
                </div>
              </div>

              <div className="precheck-item">
                <span className={`check-dot ${fullscreenReady ? 'ok' : 'wait'}`}></span>
                <div>
                  <strong>Mode Layar Penuh</strong>
                  <p>{requireFullscreen ? (isFullscreen ? 'Aktif' : 'Belum aktif') : 'Mode mobile (opsional)'}</p>
                </div>
                {requireFullscreen && !isFullscreen && <button className="btn-mini" onClick={enterFullscreen}>Aktifkan</button>}
              </div>

              <div className="precheck-item">
                <span className={`check-dot ${!cameraError ? 'ok' : 'wait'}`}></span>
                <div>
                  <strong>Kamera</strong>
                  <p>{cameraError ? 'Kamera belum tersedia' : 'Kamera aktif'}</p>
                </div>
              </div>

              <div className="precheck-item">
                <span className={`check-dot ${faceStatus === 'ok' ? 'ok' : 'wait'}`}></span>
                <div>
                  <strong>Deteksi Wajah</strong>
                  <p>{faceStatus === 'ok' ? 'Wajah terdeteksi' : 'Wajah belum terdeteksi'}</p>
                </div>
              </div>
            </div>

            <div className="precheck-camera">
              {!cameraError ? (
                <Webcam
                  ref={webcamRef}
                  audio={false}
                  width={220}
                  height={150}
                  playsInline
                  mirrored
                  videoConstraints={webcamVideoConstraints}
                  onUserMedia={handleUserMedia}
                  screenshotFormat="image/jpeg"
                  onUserMediaError={handleCameraError}
                  className="webcam-preview"
                />
              ) : (
                <div className="cam-error">❌ Kamera tidak tersedia</div>
              )}
              <canvas ref={canvasRef} style={{ display: 'none' }} />
            </div>

            <div className="rules-box precheck-rules">
              <h3>⚠️ Peraturan Psikotest</h3>
              <ul>
                {requireFullscreen ? (
                  <li>Psikotest wajib dilakukan dalam mode layar penuh</li>
                ) : (
                  <li>Untuk perangkat mobile, mode layar penuh bersifat opsional</li>
                )}
                <li>Semua aktifitas akan direkam</li>
                <li>Dilarang berpindah tab atau keluar jendela browser</li>
                <li>Kamera wajah harus aktif selama psikotest</li>
                <li>Wajah harus terdeteksi selama psikotest berlangsung</li>
                <li>Copy-paste dinonaktifkan selama psikotest</li>
                <li>Dilarang membuka aplikasi lain selama psikotest berlangsung</li>
                <li>Pastikan hanya satu wajah yang terlihat di kamera</li>
                <li>Pastikan pencahayaan ruangan cukup agar wajah tetap terdeteksi</li>
                <li>Pilih jawaban sesuai kondisi diri Anda</li>
              </ul>
            </div>

            <label className="confirm-rule">
              <input type="checkbox" checked={rulesConfirmed} onChange={(e) => setRulesConfirmed(e.target.checked)} />
              Saya sudah membaca dan menyetujui seluruh peraturan psikotest.
            </label>

            <div className="precheck-example">
              <strong>Contoh Soal A/B:</strong>
              <p>a. Saya adalah pekerja keras</p>
              <p>b. Saya tidak mudah murung</p>
              <small>Pilih pernyataan yang paling menggambarkan diri Anda.</small>
            </div>

            <button className="btn-submit start-btn" onClick={startPsychotestFlow} disabled={!canStartPsychotest}>
              Mulai Kerjakan Psikotest
            </button>
          </section>
        </div>
      ) : (
        <>
          <header className="exam-header">
            <div className="exam-title">Psikotest Kepribadian</div>
            <div className="exam-meta">
              {!requireFullscreen && <span className="violation-badge mobile-mode">📱 Mode Mobile</span>}
              <span className={`timer ${remainingSeconds <= 300 ? 'danger' : ''}`}>⏱ {formatTime(remainingSeconds)}</span>
              <span className="progress-text">{answeredCount}/{questions.length} dijawab</span>
              <span className={`violation-badge ${violationCount > 0 ? 'warn' : ''}`}>⚠️ {violationCount}/{maxViolations}</span>
              <span className={`violation-badge ${faceStatus === 'ok' ? '' : 'warn'}`}>
                {faceStatus === 'ok' ? '✅ Wajah Terdeteksi' : '⚠️ Wajah Tidak Terdeteksi'}
              </span>
            </div>
          </header>

          <div className="progress-bar">
            <div className="progress-fill" style={{ width: `${progressPercent}%` }}></div>
          </div>

          <main className="exam-body">
            <aside className="q-nav">
              <h3>Navigasi Soal</h3>
              <div className="q-grid">
                {questions.map((q, i) => (
                  <button
                    key={q.id}
                    className={`q-btn ${i === currentIdx ? 'active' : ''} ${answers[q.id] ? 'answered' : ''}`}
                    onClick={() => setCurrentIdx(i)}
                  >
                    {i + 1}
                  </button>
                ))}
              </div>
              <div className="legend">
                <span className="leg answered"></span> Dijawab
                <span className="leg active"></span> Aktif
                <span className="leg empty"></span> Belum
              </div>

              <div className="camera-box">
                <div className={`face-status ${faceStatus === 'ok' ? 'ok' : 'missing'}`}>
                  {faceStatus === 'ok' ? '✅ Wajah Terdeteksi' : '❌ Wajah Tidak Ada'}
                </div>
                {!cameraError ? (
                  <Webcam
                    ref={webcamRef}
                    audio={false}
                    width={180}
                    height={130}
                    playsInline
                    mirrored
                    videoConstraints={webcamVideoConstraints}
                    onUserMedia={handleUserMedia}
                    screenshotFormat="image/jpeg"
                    onUserMediaError={handleCameraError}
                    className="webcam-preview"
                  />
                ) : (
                  <div className="cam-error">❌ Kamera tidak tersedia</div>
                )}
                <canvas ref={canvasRef} style={{ display: 'none' }} />
              </div>
            </aside>

            <section className="q-main">
              {currentQ && (
                <div className="question-card">
                  <div className="q-number">Soal {currentIdx + 1} dari {questions.length}</div>
                  <p className="q-text">Pernyataan mana yang lebih menggambarkan Anda?</p>

                  <div className="options">
                    <button
                      type="button"
                      className={`option-btn ${String(answers[currentQ.id] || '') === 'a' ? 'selected' : ''}`}
                      onClick={() => handlePick(currentQ.id, 'a')}
                    >
                      <span className="opt-label">A</span>
                      <span className="opt-text">{currentQ.statement_a}</span>
                    </button>

                    <button
                      type="button"
                      className={`option-btn ${String(answers[currentQ.id] || '') === 'b' ? 'selected' : ''}`}
                      onClick={() => handlePick(currentQ.id, 'b')}
                    >
                      <span className="opt-label">B</span>
                      <span className="opt-text">{currentQ.statement_b}</span>
                    </button>
                  </div>

                  <div className="q-nav-btns">
                    <button
                      className="btn-nav"
                      onClick={() => setCurrentIdx((i) => Math.max(0, i - 1))}
                      disabled={currentIdx === 0}
                    >
                      ← Sebelumnya
                    </button>

                    {currentIdx < questions.length - 1 ? (
                      <button
                        className="btn-nav btn-next"
                        onClick={() => setCurrentIdx((i) => Math.min(questions.length - 1, i + 1))}
                      >
                        Berikutnya →
                      </button>
                    ) : (
                      <button
                        className="btn-submit"
                        onClick={handleSubmit}
                        disabled={submitting || answeredCount !== questions.length}
                      >
                        {submitting ? 'Menyimpan...' : 'Selesaikan Psikotest dan Lanjut ke Exam'}
                      </button>
                    )}
                  </div>
                </div>
              )}
            </section>
          </main>
        </>
      )}
    </div>
  );
}
